import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
} from "react-bootstrap";
import axiosInstance from '../../services/AxiosInstance';
import './commonstyle.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import '../tasks/tasklist.css';
import { isBranchAdmin, isDepartmentAdmin, isSubDepartmentAdmin, getCurrentUserRole } from '../../utils/roleAccess';

const DepartmentList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    // Filter departments based on search term
    if (searchTerm.trim() === '') {
      setFilteredDepartments(departments);
    } else {
      const filtered = departments.filter(department =>
        department.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (department.description && department.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (department.branch_name && department.branch_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredDepartments(filtered);
    }
  }, [departments, searchTerm]);

  useEffect(() => {
    const querySearch = searchParams.get('search') || '';
    if (querySearch !== searchTerm) {
      setSearchTerm(querySearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      const currentUserRole = getCurrentUserRole();
      
      // Build URL with role-based filtering
      let url = `/settings/departments?corporate_id=${corporateId}`;
      
      // If user has restricted access (branch_admin, department_admin, or sub_department_admin),
      // filter by their branch
      if (isBranchAdmin(currentUserRole) || isDepartmentAdmin(currentUserRole) || isSubDepartmentAdmin(currentUserRole)) {
        try {
          const userId = userData?.id;
          if (userId) {
            const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
            const userDetails = userDetailsRes.data;
            const branchId = userDetails?.Branch?.id;
            
            if (branchId) {
              url += `&branch_id=${branchId}`;
            }
          }
        } catch (err) {
          console.error('Error fetching current user details for filtering:', err);
        }
      }
      
      const res = await axiosInstance.get(url);
      const data = res.data;
      
      if (res) {
        setDepartments(data.data || data || []);
      }
    } catch (error) {
      console.error("Department list fetch error:", error);
      notifyError('Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDepartment) return;
    const userData = JSON.parse(localStorage.getItem('user'));
    setDeleteLoading(true);
    try {
      const res = await axiosInstance.put(`/settings/deactivateDepartment`, { department_id: selectedDepartment.id, updated_by: userData.id });
      
      if (res) {
        notifyTopRight('Department deleted successfully');
        fetchDepartments(); // Refresh the list
        setShowDeleteModal(false);
        setSelectedDepartment(null);
      }
    } catch (error) {
      console.error('Delete department error:', error);
      if (error.status === 409) {
        notifyError('Cannot delete department with tasks or users');
      } else {
        notifyError(error.response?.data?.error || 'Failed to delete department');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = (department) => {
    navigate(`/settings/editDepartment/${department.id}`, { state: { department } });
  };

  // Define columns for CommonTable
  const columns = [
    {
      key: 'index',
      header: '#',
      accessor: (row, index) => index + 1,
    },
    {
      key: 'name',
      header: 'Department Name',
      accessor: 'name',
    },
    {
      key: 'branch',
      header: 'Branch',
      accessor: (row) => row.branch_name || 'N/A',
    },
  ];

  // Define actions for CommonTable
  const getActions = (row, rowIndex) => {
    return [
      {
        label: 'Edit',
        icon: 'fas fa-edit',
        onClick: () => handleEdit(row),
      },
      {
        label: 'Delete',
        icon: 'fas fa-trash',
        className: 'text-danger',
        onClick: () => {
          setSelectedDepartment(row);
          setShowDeleteModal(true);
        },
      },
    ];
  };

  usePageToolbar(null, []);

  return (
    <Row style={{marginBottom: "0px !important"}}>
      <ToastContainer />
      <Col lg={12}>
        <Card className="user-list-card">
          <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1.5rem' }}>
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <div className="row">
                <CommonTable
                  columns={columns}
                  data={filteredDepartments}
                  actions={getActions}
                  emptyMessage={departments.length === 0 ? 'No departments found' : 'No departments match your search criteria'}
                  emptyStateIcon="fas fa-sitemap"
                  getRowId={(row, index) => row.id || index}
                  maxHeight="70vh"
                  minHeight="400px"
                />
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the department "{selectedDepartment?.name}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <BackToTop />
      <InternalFooter />
    </Row>
  );
};

export default DepartmentList;
